import type { AxiosResponse } from 'axios';
import interceptor from '../common/interceptor';
import { APIPaths } from '../common/constants';
import {
  BeaconResponseData,
  PaginatedApiResponse,
  TrackedBeaconEvent,
} from './types';
import {
  isInclusionPayload,
  type UserSelectionPayload,
} from '@/pages/Settings/Users/ActivityFeed/types';
import { OperationType } from '@/common/enum';

/** Backend's own field names for the users filter — snake_case, no `single_mode`. */
export type UserSelectionWirePayload =
  | { select_all: true; excluded_user_ids: string[] }
  | { select_all: false; user_ids: string[] };

const toWireUsersFilter = (
  payload: UserSelectionPayload
): UserSelectionWirePayload =>
  isInclusionPayload(payload)
    ? { select_all: false, user_ids: payload.userIds }
    : { select_all: true, excluded_user_ids: payload.excludedUserIds };

interface UserActivityFilters {
  timezone?: string;
  single_mode?: boolean;
  users: UserSelectionPayload;
  event_type?: {
    select_all: boolean;
    include_ids: string[];
  };
  time_window?: {
    preset?: string;
    start?: string;
    end?: string;
  };
}

export interface UserActivityPayload extends UserActivityFilters {
  page: number;
  page_size: number;
}

/** Export has same filters, applied to every
 * matching row, not just the page currently on screen. */
export type UserActivityExportPayload = UserActivityFilters;

/** One activity row exactly as the backend sends it (snake_case) — mapped
 * to this app's own `ActivityEvent` shape by the caller. */
export interface ActivityEventDTO {
  id: string;
  user_id: string | null;
  user_name: string;
  type_id: string;
  action: string;
  detail: string;
  date: string;
  time: string;
  shipment_id: string | null;
  coc_id: string | null;
  notification_source?: string | null;
  sentence: string | null;
  /** "customer_leg" / "execution_leg" on a shipment-viewed row for an account
   * with legs enabled, else null. */
  leg_type?: string | null;
  /** Set on an execution-leg row, which has no shipment_id. */
  execution_leg_id?: string | null;
}

export interface ActivityDayBucketDTO {
  date: string;
  activity: ActivityEventDTO[];
}

export type GetUserActivityResponse = PaginatedApiResponse<
  ActivityDayBucketDTO[]
>;

export const GetUserActivity = (
  token: string | null,
  model: UserActivityPayload
): Promise<GetUserActivityResponse> => {
  const { users, ...rest } = model;
  const wireModel = { ...rest, users: toWireUsersFilter(users) };
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Post',
        APIPaths.userActivityLog,
        wireModel,
        token ?? undefined
      )
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('Error while get user activity', error?.response);
        reject(error?.response);
      });
  });
};

// Backend generates the actual file, this just posts the filters
export const ExportUserActivity = (
  token: string | null,
  model: UserActivityExportPayload
): Promise<AxiosResponse<Blob>> => {
  const { users, ...rest } = model;
  const wireModel = { ...rest, users: toWireUsersFilter(users) };
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGetDownload(
        'Post',
        APIPaths.userActivityExport,
        token ?? undefined,
        wireModel
      )
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('Error while download user activity', error?.response);
        reject(error?.response);
      });
  });
};

/** Users filter chip's own listing — deliberately not `GetALLUsers`
 * (Settings > Users table's endpoint): that one only returns active/disabled
 * depending on a toggle, never both. This one always returns everyone
 * (active, disabled, never-logged-in), tagged via `type`. */
export interface ActivityUserLite {
  id: string;
  name: string;
  type: 'Active' | 'Disabled' | 'No activity';
  timezone?: string | null;
}

export type GetActivityUsersLiteResponse = PaginatedApiResponse<
  ActivityUserLite[]
>;

export const GetActivityUsersLite = (
  token: string,
  page?: number,
  search?: string
): Promise<GetActivityUsersLiteResponse> => {
  const params = new URLSearchParams();
  if (page != null) params.append('page', page.toString());
  if (search) params.append('search', search);

  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        `${APIPaths.userActivityUsersLite}?${params.toString()}`,
        token,
        null
      )
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('Error while fetching activity users', error?.response);
        reject(error?.response);
      });
  });
};

/** Events filter chip's catalog — the group/leaf structure and labels come
 * from the backend now, not a hardcoded frontend list, so a new event type
 * shows up here without a frontend deploy. `value` (backend's field name)
 * is mapped to this app's own `id` convention by the caller. */
export interface EventTypeGroupDTO {
  value: string;
  label: string;
  children?: { value: string; label: string }[];
}

export type GetActivityEventTypesResponse = PaginatedApiResponse<
  EventTypeGroupDTO[]
>;

export const GetActivityEventTypes = (
  token: string,
  search = ''
): Promise<GetActivityEventTypesResponse> => {
  const params = new URLSearchParams();
  if (search.trim()) params.append('search', search.trim());
  const query = params.toString();
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        `${APIPaths.userActivityEventTypes}${query ? `?${query}` : ''}`,
        token,
        null
      )
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error(
          'Error while fetching activity event types',
          error?.response
        );
        reject(error?.response);
      });
  });
};

export const postActivityBeacon = (
  events: TrackedBeaconEvent[]
): Promise<AxiosResponse<{ data?: BeaconResponseData }>> => {
  return interceptor.axiosPost(
    'Post',
    APIPaths.activityBeacon,
    events,
    undefined,
    null,
    OperationType.SilentWrite,
    true
  );
};
